import { useCallback } from "react";

import clsx from "clsx";

import { Column } from "@/components/containers";
import { ImageLegacy, MoneySpan } from "@/components/displays";
import { SelectedProducts } from "@/components/domains/lp/OnePageCheckoutForm/SetSelector/types";
import { isQuantityOptionsEnabled, makeQuantityOptions } from "@/components/domains/product/hooks";
import { getQuantityRestrictMessages } from "@/components/domains/product/hooks";
import { Selector } from "@/components/inputs";
import { subscriptionProductAnnotation } from "@/configs/products";
import { PRODUCT_SALE_PARAMETER } from "@/configs/system";
import { isActiveProductCaution } from "@/features/product-caution";
import { useListProductCautions } from "@/generated/open-api/product-caution/product-caution";
import { DiscountPlanName } from "@/generated/open-api/schemas";
import { CartModel } from "@/models/cart/type";
import { ProductModel } from "@/models/product/type";
import { formatCurrency } from "@/utils/currency";

import styles from "./GroupedProduct.module.scss";

interface GroupedProductProps {
  cartInfo?: CartModel;
  groupedProducts: Record<string, ProductModel[] | undefined>;
  selectedProducts: SelectedProducts;
  onChange: (variantId: number) => (value: number) => void;
  onClickDelete: (variantId: number) => () => void;
  isFreeze?: boolean;
  discountPlanName?: DiscountPlanName;
}

export function GroupedProduct({
  cartInfo,
  groupedProducts,
  selectedProducts,
  onChange,
  onClickDelete,
  isFreeze,
  discountPlanName,
}: GroupedProductProps): React.ReactNode {
  const validPurchase = isFreeze ? cartInfo?.validFreezePurchase : cartInfo?.validPurchase;

  const { data: productCautionsData } = useListProductCautions({});
  const getActiveCautions = useCallback(
    (product: ProductModel) => {
      return (productCautionsData?.data ?? []).filter((caution) =>
        isActiveProductCaution(caution, {
          area: "LP_SELECT",
          temperature: product.isFreeze ? "FROZEN" : "NORMAL",
          variantId: product.variantId,
        })
      );
    },
    [productCautionsData?.data]
  );

  return (
    <div
      className={clsx("mg__top__m mg__bottom__m", isFreeze ? styles.freezeBox : styles.normalBox)}
    >
      <p
        className={clsx(
          "text__m text__bold text__white",
          isFreeze ? styles.freezeTitle : styles.normalTitle
        )}
      >
        {isFreeze ? "冷凍配送" : "常温配送"}
      </p>
      {Object.entries(groupedProducts).map(([label, products = []]) => (
        <section key={label} className="mg__top__m">
          <p className="text__m text__bold">{label}</p>
          {products.map((product) => {
            const targetProductAnnotation = subscriptionProductAnnotation.find(
              ({ sku }) => sku === product.sku
            );
            const productPrice =
              Number(
                product.prices[discountPlanName || "DEFAULT"]?.first_subscription_discount_price
              ) || 0;

            const options = makeQuantityOptions({ product });
            const enabled = isQuantityOptionsEnabled(options);
            const quantityRestrictMessages = getQuantityRestrictMessages({ product });

            const activeCautions = getActiveCautions(product);
            return (
              <div key={product.variantId} className={styles.productBorder}>
                <div className={clsx("pd__top__m", styles.flex)}>
                  <div>
                    <ImageLegacy
                      src={product.images[product.variantImageIndex]}
                      alt={product.title}
                      className={styles.productThumbnail}
                      size={{ width: 80, height: 80 }}
                    />
                  </div>
                  <div className={styles.product}>
                    <p className="text__m">{product.title}</p>
                    {product.meal !== "組" && <p className="text__m">{product.variantTitle}</p>}
                    <p className="mg__top__s">
                      <span className={styles.discountTag}>初回割引</span>
                      <span className="wsnr">
                        <MoneySpan
                          className="text__red"
                          priceClassName="text__m  text__bold"
                          suffix={`/${product.meal}`}
                          suffixClassName="text__s"
                        >
                          {productPrice}
                        </MoneySpan>
                      </span>
                    </p>
                  </div>
                </div>
                <div className={clsx("pd__bottom__m", styles.flex, styles.flexCenter)}>
                  <div className={styles.selector}>
                    <Selector
                      // 選択されていない場合、数量を「0」にする
                      value={selectedProducts[product.variantId] || 0}
                      onChange={onChange(product.variantId)}
                      options={options}
                      disabled={!enabled}
                    />
                  </div>
                  <span className="text__m">{product.meal}</span>
                  {!!selectedProducts[product.variantId] && (
                    <button
                      className="btn inline-block mini round gray text__black text__bold align-middle mx-[5px]"
                      onClick={onClickDelete(product.variantId)}
                    >
                      削除
                    </button>
                  )}
                  <MoneySpan suffix="*" className={clsx("text__l text__bold", styles.flexRight)}>
                    {(selectedProducts[product.variantId] ?? 0) * productPrice}
                  </MoneySpan>
                </div>
                {activeCautions.length > 0 && (
                  <Column className="mg__bottom__m">
                    {activeCautions.map((caution) => (
                      <p key={caution.id} className="text__s text__red">
                        {caution.message}
                      </p>
                    ))}
                  </Column>
                )}
                {quantityRestrictMessages.length > 0 && (
                  <Column className="mg__bottom__m">
                    {quantityRestrictMessages.map((message) => (
                      <p key={message} className="text__s text__red">
                        {message}
                      </p>
                    ))}
                  </Column>
                )}
                {targetProductAnnotation?.annotation && (
                  <p
                    className={clsx(
                      "text__s text__gray__dark pd__bottom__m",
                      styles.productAnnotation
                    )}
                  >
                    {targetProductAnnotation.annotation}
                  </p>
                )}
              </div>
            );
          })}
        </section>
      ))}

      {!validPurchase && (
        <div className={clsx("mg__top__m", styles.cartError)}>
          <p className="text__m text__center text__white">
            {`合計金額${formatCurrency(isFreeze ? PRODUCT_SALE_PARAMETER.minDeliveryAmountFrozen : PRODUCT_SALE_PARAMETER.minDeliveryAmountNormal).replace("¥", "")}円以上から`}
            <br />
            ご購入いただけます。
          </p>
        </div>
      )}

      <dl className={clsx("mg__top__s", styles.totalPriceTable)}>
        <dt className="text__left">
          <span className="text__m">小計：</span>
        </dt>
        <dd className="text__right">
          <MoneySpan className="text__l text__bold text__red">
            {isFreeze ? cartInfo?.freezeProductTotalPrice : cartInfo?.normalProductTotalPrice}
          </MoneySpan>
        </dd>
      </dl>
      <dl className={clsx("mg__top__s", styles.totalPriceTable)}>
        <dt className="text__left">
          <span className="text__m">配送料：</span>
        </dt>
        <dd className="text__right">
          <MoneySpan
            className={clsx(
              "text__l text__bold",
              (isFreeze ? !cartInfo?.freezeShippingFee : !cartInfo?.normalShippingFee) &&
                "text__red"
            )}
            suffix="**"
          >
            {isFreeze ? cartInfo?.freezeShippingFee : cartInfo?.normalShippingFee}
          </MoneySpan>
        </dd>
      </dl>
    </div>
  );
}
